import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useKanban } from '../../contexts/KanbanContext';
import { useModal } from '../../contexts/ModalContext';
import { useAuth } from '../../contexts/AuthContext';
import tarefaService from '../../services/tarefaService';
import { toast } from 'react-toastify';
import { Tarefa, StatusTarefa } from '../../types';
import { mapearComentarios } from './ComentarioMapper';
import api from '../../services/api';

/**
 * Componente que carrega uma tarefa específica e abre o modal de detalhes
 * 
 * Correções implementadas:
 * 1. Tratamento especial para usuários admin usando endpoint direto da API
 * 2. Limitação do número de tentativas para evitar loops infinitos
 * 3. Melhor tratamento de erros e feedback para o usuário
 */
const TarefaRedirecionador: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { abrirModalDetalhes, modalDetalhesAberto } = useModal();
  const { usuario } = useAuth();
  const [tarefaCarregada, setTarefaCarregada] = useState<Tarefa | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [tentativasDeCarregamento, setTentativasDeCarregamento] = useState<number>(0);
  
  useEffect(() => {
    // Limitar o número máximo de tentativas para evitar loops infinitos
    const MAX_TENTATIVAS = 2;
    
    const carregarTarefa = async () => {
      if (!id) {
        setErro('ID da tarefa não fornecido');
        navigate('/');
        return;
      }
      
      console.log(`Tentando carregar tarefa com ID: ${id}`);
      
      try {
        setCarregando(true);
        
        // Verificar se já atingimos o número máximo de tentativas
        if (tentativasDeCarregamento >= MAX_TENTATIVAS) {
          console.log(`Máximo de ${MAX_TENTATIVAS} tentativas atingido. Abortando carregamento.`);
          setErro(`Não foi possível carregar a tarefa após ${MAX_TENTATIVAS} tentativas.`);
          setCarregando(false);
          return;
        }
        
        // Verificar se é a primeira tentativa ou se já tentamos mais de uma vez
        if (tentativasDeCarregamento > 0) {
          console.log(`Tentativa ${tentativasDeCarregamento + 1} de carregar a tarefa ${id}`);
        }
        
        // Incrementar o contador de tentativas
        setTentativasDeCarregamento(prev => prev + 1);
        
        // Verificar se o ID é válido
        if (!id || id === 'undefined' || id === 'null') {
          console.error('ID de tarefa inválido:', id);
          setErro('ID de tarefa inválido');
          setCarregando(false);
          return;
        }
        
        // Se for admin, usar o endpoint direto da API para evitar problemas de permissão
        let resposta;
        if (usuario?.tipo === 'admin' && tentativasDeCarregamento > 0) {
          console.log('Usuário é admin, usando endpoint direto da API');
          const response = await api.get(`/tarefas/${id}`);
          resposta = response.data.tarefa || response.data;
        } else {
          // Carregar a tarefa específica da API usando o serviço padrão
          resposta = await tarefaService.obterTarefa(id);
        }
        
        console.log('Resposta da API:', resposta);
        
        // Verificar se a resposta tem o formato esperado
        if (resposta) {
          // Construir objeto de tarefa com valores padrão para campos ausentes
          const tarefaCompleta: Tarefa = {
            id: resposta.id || id,
            titulo: resposta.titulo || 'Sem título',
            descricao: resposta.descricao || '',
            status: (resposta.status as StatusTarefa) || 'Pendente',
            prioridade: resposta.prioridade || 'media',
            dataCriacao: resposta.data_criacao || resposta.dataCriacao || new Date().toISOString(),
            dataVencimento: resposta.data_vencimento || resposta.dataVencimento || null,
            departamentoId: resposta.departamento_id || resposta.departamentoId || '',
            
            // Construir objeto de responsável
            responsavel: {
              id: resposta.responsavel_id || (resposta.responsavel && resposta.responsavel.id) || '',
              nome: resposta.responsavel_nome || (resposta.responsavel && resposta.responsavel.nome) || 'Não atribuído',
              email: resposta.responsavel_email || (resposta.responsavel && resposta.responsavel.email) || '',
              cargo: (resposta.responsavel && resposta.responsavel.cargo) || '',
              departamentoId: (resposta.responsavel && resposta.responsavel.departamentoId) || '',
              ehLider: (resposta.responsavel && resposta.responsavel.ehLider) || false,
              tipo: (resposta.responsavel && resposta.responsavel.tipo) || 'comum',
              avatar: '/profilepicture.png', // Usando imagem padrão
              tarefasAtivas: (resposta.responsavel && resposta.responsavel.tarefasAtivas) || 0,
              tarefasConcluidas: (resposta.responsavel && resposta.responsavel.tarefasConcluidas) || 0,
              dataCriacao: (resposta.responsavel && resposta.responsavel.dataCriacao) || ''
            },
            
            // Construir objeto de criador
            criador: {
              id: resposta.criador_id || (resposta.criador && resposta.criador.id) || '',
              nome: resposta.criador_nome || (resposta.criador && resposta.criador.nome) || 'Desconhecido',
              email: resposta.criador_email || (resposta.criador && resposta.criador.email) || '',
              cargo: (resposta.criador && resposta.criador.cargo) || '',
              departamentoId: (resposta.criador && resposta.criador.departamentoId) || '',
              ehLider: (resposta.criador && resposta.criador.ehLider) || false,
              tipo: (resposta.criador && resposta.criador.tipo) || 'comum',
              avatar: '/profilepicture.png', // Usando imagem padrão
              tarefasAtivas: (resposta.criador && resposta.criador.tarefasAtivas) || 0,
              tarefasConcluidas: (resposta.criador && resposta.criador.tarefasConcluidas) || 0,
              dataCriacao: (resposta.criador && resposta.criador.dataCriacao) || ''
            },
            
            // Usar o utilitário de mapeamento de comentários para garantir consistência
            comentarios: mapearComentarios(resposta.comentarios || [])
          };
          
          console.log('Tarefa completa montada:', tarefaCompleta);
          setTarefaCarregada(tarefaCompleta);
          
          // Abrir o modal de detalhes com a tarefa carregada
          abrirModalDetalhes(tarefaCompleta);
          
          // Não redirecionar para manter o ID da tarefa na URL
          // Isso permite que o link continue funcionando para compartilhamento
          // e que o usuário possa atualizar a página mantendo o contexto
        } else {
          setErro('Tarefa não encontrada ou dados incompletos');
          toast.error('Tarefa não encontrada');
          navigate('/');
        }
      } catch (error: any) {
        console.error('Erro ao carregar tarefa:', error);
        const mensagemErro = error.response?.data?.message || error.message || 'Erro desconhecido';
        setErro(`Erro ao carregar tarefa: ${mensagemErro}`);
        toast.error(`Erro ao carregar tarefa: ${mensagemErro}`);
        navigate('/');
      } finally {
        setCarregando(false);
      }
    };
    
    carregarTarefa();
  }, [id, abrirModalDetalhes, navigate, usuario?.tipo]); // Adicionado usuario?.tipo como dependência

  // Quando o modal é fechado após carregar a tarefa, retornar à página anterior
  useEffect(() => {
    if (!modalDetalhesAberto && tarefaCarregada) {
      navigate(-1);
    }
  }, [modalDetalhesAberto, tarefaCarregada, navigate]);
  
  // Renderiza um indicador de carregamento enquanto busca a tarefa ou uma mensagem de erro
  return (
    <div className="flex flex-col items-center justify-center h-screen bg-gray-100">
      {carregando ? (
        <div className="bg-white p-8 rounded-lg shadow-lg text-center">
          <div className="flex justify-center mb-4">
            <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-blue-600"></div>
          </div>
          <h2 className="text-xl font-semibold text-gray-800 mb-2">Abrindo tarefa</h2>
          <p className="text-gray-600">Carregando detalhes da tarefa #{id}...</p>
          <p className="text-sm text-gray-500 mt-4">Você será redirecionado automaticamente</p>
        </div>
      ) : erro ? (
        <div className="text-center p-8 bg-white rounded-lg shadow-lg max-w-md">
          <div className="flex justify-center mb-4">
            <svg className="w-16 h-16 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h3 className="text-xl font-semibold text-gray-800 mb-2">Erro ao carregar tarefa</h3>
          <p className="text-gray-600 mb-4">{erro}</p>
          <button 
            onClick={() => navigate('/')} 
            className="mt-2 px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            Voltar para o Dashboard
          </button>
        </div>
      ) : (
        <div className="bg-white p-8 rounded-lg shadow-lg text-center">
          <p className="text-gray-600">Tarefa carregada com sucesso!</p>
        </div>
      )}
    </div>
  );
};

export default TarefaRedirecionador;
